import { useCallback, useEffect, useState } from 'react'

import {
  AIFeatureExecutionResolvedBusinessEventSource,
  BusinessEvent,
  EmailResolvedBusinessEventSource,
  ResolvedBusinessEventSource,
} from '../../../models/BusinessEvent'

import { resetBusinessEvents, selectBusinessEvents, setBusinessEvents, setBusinessEventsLoading } from '../../../store/businessEvent/businessEvent'
import { useLazyGetAllBusinessEventsQuery } from '../../../store/ai/businessEvent'
import { useLazyGetRoomSessionQuery } from '../../../store/media/media'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'

import { getBusinessEventSourceKey } from './utils'
import { useLazyGetEmailQuery } from '../../../store/integrations/integrations'

export const useInitializeContactBusinessEvents = (contactId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerGetBusinessEvents] = useLazyGetAllBusinessEventsQuery()

  useEffect(() => {
    const fetchBusinessEvents = async () => {
      dispatch(setBusinessEventsLoading(true))
      try {
        const { data: businessEvents } = await triggerGetBusinessEvents({ accountId, clientId: contactId }).unwrap()
        dispatch(setBusinessEvents(businessEvents))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setBusinessEventsLoading(false))
      }
    }

    fetchBusinessEvents()
  }, [accountId, contactId, dispatch, triggerGetBusinessEvents])

  useEffect(() => {
    return () => {
      dispatch(resetBusinessEvents())
    }
  }, [dispatch])
}

interface UseBusinessEventSourcesParams {
  skip?: boolean
}

export const useBusinessEventSources = ({ skip = false }: UseBusinessEventSourcesParams = {}) => {
  const accountId = useAppSelector(selectAccountId)
  const businessEvents = useAppSelector(selectBusinessEvents)

  const [triggerGetRoomSession] = useLazyGetRoomSessionQuery()
  const [triggerGetEmail] = useLazyGetEmailQuery()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [sources, setSources] = useState<ResolvedBusinessEventSource[]>([])

  const resolveAiFeatureExecutionBusinessEventSource = useCallback(
    async (roomId: string, sessionId: string): Promise<AIFeatureExecutionResolvedBusinessEventSource | null> => {
      try {
        const { data: roomSessionResponse } = await triggerGetRoomSession({ accountId, roomId, sessionId }, true)
        if (!roomSessionResponse || !roomSessionResponse.data) {
          return null
        }
        return { sourceKey: `aiFeatureExecution|${roomId}|${sessionId}`, type: 'aiFeatureExecution', source: roomSessionResponse.data }
      } catch (e) {
        console.error(e)
      }
      return null
    },
    [accountId, triggerGetRoomSession],
  )

  const resolveEmailBusinessEventSource = useCallback(
    async (emailConnector: string, emailId: string): Promise<EmailResolvedBusinessEventSource | null> => {
      try {
        const { data: integrationEmail } = await triggerGetEmail({ accountId, emailConnector, emailId }, true)
        if (!integrationEmail) {
          return null
        }
        return { sourceKey: `email|${emailConnector}|${emailId}`, type: 'email', source: integrationEmail }
      } catch (e) {
        console.error(e)
      }
      return null
    },
    [accountId, triggerGetEmail],
  )

  const getBusinessEventSourceResolver = useCallback(
    (businessEvent: BusinessEvent): Promise<ResolvedBusinessEventSource | null> => {
      const { source } = businessEvent
      if (source.type === 'email') {
        const { emailConnector, emailId } = source
        return resolveEmailBusinessEventSource(emailConnector, emailId)
      }
      const { roomId, sessionId } = source
      return resolveAiFeatureExecutionBusinessEventSource(roomId, sessionId)
    },
    [resolveAiFeatureExecutionBusinessEventSource, resolveEmailBusinessEventSource],
  )

  useEffect(() => {
    const resolveBusinessEventSources = async (businessEventSourceResolvers: Promise<ResolvedBusinessEventSource | null>[]) => {
      setIsLoading(true)
      try {
        const businessEventSourceResolverResponses = await Promise.all(businessEventSourceResolvers)
        const resolvedBusinessEventSources = businessEventSourceResolverResponses.reduce<ResolvedBusinessEventSource[]>(
          (acc, response) => (response ? [...acc, response] : acc),
          [],
        )
        if (!resolvedBusinessEventSources.length) {
          return
        }
        setSources((prev) => [...prev, ...resolvedBusinessEventSources])
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    }

    if (skip) {
      return
    }

    const existingSourceKeys = sources.map(({ sourceKey }) => sourceKey)

    const businessEventSourceDescriptors = businessEvents.reduce<
      {
        sourceKey: string
        resolver: Promise<ResolvedBusinessEventSource | null>
      }[]
    >((acc, businessEvent) => {
      const sourceKey = getBusinessEventSourceKey(businessEvent)
      const isUnique = !acc.find((e) => e.sourceKey === sourceKey)
      if (existingSourceKeys.includes(sourceKey) || !isUnique) {
        return acc
      }
      const businessEventSourceResolver = getBusinessEventSourceResolver(businessEvent)
      return [...acc, { sourceKey, resolver: businessEventSourceResolver }]
    }, [])

    const businessEventSourceResolvers = businessEventSourceDescriptors.map(({ resolver }) => resolver)
    if (!businessEventSourceResolvers.length) {
      return
    }

    resolveBusinessEventSources(businessEventSourceResolvers)
  }, [sources, businessEvents, getBusinessEventSourceResolver, skip])

  return { sources, isLoading }
}
